import React, { useState } from 'react';
import { formatUnits } from 'viem';
import { useAccount } from 'wagmi';
import { useEscrowDetail, type EscrowStatus, type EscrowEvent } from '../../contracts/useEscrowDetail';
import { formatCurrency } from '../utils/format';

export interface EscrowDetailViewProps {
  platformFeeBps: number;
  onDispute?: (escrowId: number) => void;
  onReclaim?: (escrowId: number) => void;
  className?: string;
}

// ---------- Status styling ----------

const STATUS_CONFIG: Record<EscrowStatus, { label: string; bg: string; text: string }> = {
  ACTIVE: { label: 'Active', bg: 'bg-green-600', text: 'text-green-400' },
  RELEASED: { label: 'Released', bg: 'bg-blue-600', text: 'text-blue-400' },
  DISPUTED: { label: 'Disputed', bg: 'bg-red-600', text: 'text-red-400' },
  RECLAIMED: { label: 'Reclaimed', bg: 'bg-yellow-600', text: 'text-yellow-400' },
};

const EVENT_CONFIG: Record<EscrowEvent['type'], { label: string; color: string }> = {
  Created: { label: 'Escrow Created', color: 'text-green-400' },
  Released: { label: 'Funds Released', color: 'text-blue-400' },
  Disputed: { label: 'Disputed', color: 'text-red-400' },
  Reclaimed: { label: 'Funds Reclaimed', color: 'text-yellow-400' },
  DisputeResolved: { label: 'Dispute Resolved', color: 'text-purple-400' },
};

// ---------- Helpers ----------

function truncateAddress(addr: string): string {
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function truncateHash(hash: string): string {
  return `${hash.slice(0, 10)}...${hash.slice(-6)}`;
}

function formatTimestamp(ts: bigint): string {
  return new Date(Number(ts) * 1000).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function timeRemaining(deadline: bigint): string {
  const now = BigInt(Math.floor(Date.now() / 1000));
  if (deadline <= now) return 'Expired';
  const diff = Number(deadline - now);
  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h remaining`;
  const minutes = Math.floor((diff % 3600) / 60);
  return `${hours}h ${minutes}m remaining`;
}

// ---------- Component ----------

export function EscrowDetailView({
  platformFeeBps,
  onDispute,
  onReclaim,
  className = '',
}: EscrowDetailViewProps) {
  const [inputId, setInputId] = useState('');
  const [lookupId, setLookupId] = useState<number | null>(null);
  const { address } = useAccount();

  const { escrow, events, isLoading, isError, notFound, refetch } = useEscrowDetail(lookupId);

  const handleLookup = (e: React.FormEvent) => {
    e.preventDefault();
    const id = Number(inputId);
    if (id >= 1) setLookupId(id);
  };

  const isDepositor = escrow && address && escrow.depositor.toLowerCase() === address.toLowerCase();

  return (
    <div className={`bg-gray-900 border border-gray-800 rounded-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-white">Escrow Detail</h2>
        {lookupId !== null && (
          <button
            onClick={refetch}
            className="text-xs text-gray-400 hover:text-white transition-colors"
          >
            Refresh
          </button>
        )}
      </div>

      {/* Lookup bar */}
      <form onSubmit={handleLookup} className="flex gap-3 mb-6">
        <input
          type="number"
          min="1"
          value={inputId}
          onChange={e => setInputId(e.target.value)}
          placeholder="Escrow ID"
          className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-purple-500"
        />
        <button
          type="submit"
          disabled={!inputId || Number(inputId) < 1}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold rounded transition-colors text-sm"
        >
          Look Up
        </button>
      </form>

      {/* States */}
      {isLoading && lookupId !== null && (
        <div className="text-center py-8 text-gray-400 text-sm">Loading escrow #{lookupId}...</div>
      )}
      {isError && (
        <div className="text-center py-8 text-red-400 text-sm">Error reading escrow data</div>
      )}
      {notFound && (
        <div className="text-center py-8 text-gray-500 text-sm">Escrow #{lookupId} not found</div>
      )}

      {/* Detail card */}
      {escrow && lookupId !== null && (
        <div className="space-y-4">
          {/* Status badge + ID */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-white font-semibold text-lg">#{lookupId}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-semibold text-white ${STATUS_CONFIG[escrow.status].bg}`}>
                {STATUS_CONFIG[escrow.status].label}
              </span>
            </div>
            {escrow.status === 'ACTIVE' && (
              <span className="text-xs text-gray-400">
                {timeRemaining(escrow.deadline)}
              </span>
            )}
          </div>

          {/* Detail grid */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-800/50 rounded-lg">
            <DetailRow label="Depositor" value={
              <a
                href={`https://sepolia.basescan.org/address/${escrow.depositor}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-400 hover:text-purple-300 font-mono text-sm"
              >
                {truncateAddress(escrow.depositor)}
              </a>
            } />
            <DetailRow label="Agent ID" value={
              <span className="text-white text-sm">{escrow.agentId.toString()}</span>
            } />
            <DetailRow label="Amount" value={
              <span className="text-white font-semibold text-sm">
                {formatCurrency(Number(escrow.amountFormatted))}
              </span>
            } />
            <DetailRow label="Fee / Payout" value={
              <span className="text-gray-300 text-sm">
                {formatCurrency(Number(escrow.amountFormatted) * platformFeeBps / 10000)} fee
                {' / '}
                {formatCurrency(Number(escrow.amountFormatted) * (1 - platformFeeBps / 10000))} payout
              </span>
            } />
            <DetailRow label="Created" value={
              <span className="text-gray-300 text-sm">{formatTimestamp(escrow.createdAt)}</span>
            } />
            <DetailRow label="Deadline" value={
              <span className="text-gray-300 text-sm">{formatTimestamp(escrow.deadline)}</span>
            } />
            <DetailRow label="Task Hash" value={
              <span className="text-gray-400 font-mono text-xs">{truncateHash(escrow.taskHash)}</span>
            } span2 />
          </div>

          {/* Actions (depositor only, active escrows) */}
          {isDepositor && escrow.status === 'ACTIVE' && (onDispute || onReclaim) && (
            <div className="flex gap-3">
              {onDispute && (
                <button
                  onClick={() => onDispute(lookupId)}
                  className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-semibold rounded transition-colors"
                >
                  Dispute
                </button>
              )}
              {onReclaim && (
                <button
                  onClick={() => onReclaim(lookupId)}
                  className="flex-1 px-3 py-2 bg-yellow-600 hover:bg-yellow-500 text-white text-sm font-semibold rounded transition-colors"
                >
                  Reclaim
                </button>
              )}
            </div>
          )}

          {/* Event timeline */}
          {events.length > 0 && (
            <div>
              <div className="text-sm text-gray-400 font-medium mb-3">Event History</div>
              <div className="space-y-2">
                {events.map((evt, i) => (
                  <EventRow key={`${evt.transactionHash}-${i}`} event={evt} />
                ))}
              </div>
            </div>
          )}
          {!isLoading && events.length === 0 && (
            <div className="text-xs text-gray-600 text-center py-2">No events found on-chain</div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Sub-components ----------

function DetailRow({
  label,
  value,
  span2 = false,
}: {
  label: string;
  value: React.ReactNode;
  span2?: boolean;
}) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      {value}
    </div>
  );
}

function EventRow({ event }: { event: EscrowEvent }) {
  const cfg = EVENT_CONFIG[event.type];

  let detail = '';
  if (event.type === 'Created' && event.args.amount) {
    detail = `${formatCurrency(Number(formatUnits(event.args.amount as bigint, 6)))} deposited`;
  } else if (event.type === 'Released' && event.args.payout) {
    const payout = formatCurrency(Number(formatUnits(event.args.payout as bigint, 6)));
    const fee = formatCurrency(Number(formatUnits((event.args.fee as bigint) ?? 0n, 6)));
    detail = `${payout} paid (${fee} fee)`;
  } else if (event.type === 'Reclaimed' && event.args.amount) {
    detail = `${formatCurrency(Number(formatUnits(event.args.amount as bigint, 6)))} returned`;
  } else if (event.type === 'DisputeResolved') {
    detail = event.args.released ? 'Released to recipient' : 'Returned to depositor';
  }

  return (
    <div className="flex items-center gap-3 p-2 bg-gray-800/30 rounded text-sm">
      <span className={`font-medium ${cfg.color}`}>{cfg.label}</span>
      {detail && <span className="text-gray-400 text-xs">{detail}</span>}
      <span className="ml-auto text-gray-600 text-xs font-mono">
        block {event.blockNumber.toString()}
      </span>
      {event.transactionHash && (
        <a
          href={`https://sepolia.basescan.org/tx/${event.transactionHash}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-600 hover:text-gray-400 text-xs"
        >
          tx
        </a>
      )}
    </div>
  );
}
